import { Injectable } from '@angular/core';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { SseService } from './SseService';
import { OAuthService } from './oauth.service';
import { BrokerAdapterFactory } from './broker-adapter-factory.service';
import { BrokerAdapter, BrokerOrder } from './broker-adapter.interface';
import { isooperDeOrderRef, sentidoDeOrderRef } from '../shared/utils/order-ref.util';
import { OrigenOrden } from '../shared/pipes/origen-orden.pipe';

export interface ResultadoSalida {
  ok: boolean;
  msg: string;
}

/**
 * Posición de un conid en IBKR frente a lo que la BD tiene abierto. IBKR compensa por conid: una
 * entrada bajista del mismo valor reduce o anula la alcista, así que se compara el neto, no el signo.
 * `neto` = posición del broker; `esperado` = suma con signo (+ alcista / − bajista) de las abiertas.
 */
export interface LibroConid {
  neto: number;
  esperado: number;
}

/** Status de tbl_orden (en mayúsculas) que cuentan como ejecutados: IBKR `Filled`, MetaAPI `ORDER_STATE_FILLED`. */
export const ESTADOS_FILLED = ['FILLED', 'ORDER_STATE_FILLED'];

/** Señal de salida del modelo que no llegó al broker (SALIDA de tmp_operacion sin orden en tbl_orden). */
export interface SalidaPendiente {
  /** Estado de envío de tmp_operacion: PENDIENTE-ENVIO, AUTOMATIZACION-DETENIDA, ERROR... */
  estadoEnvio: string;
  fechaOperacion: any;
}

/** Contexto de una tanda de salidas: las abiertas de la cartera y el libro de cada conid ya leído. */
export interface ContextoSalidaIbkr {
  abiertas: any[];
  libros: Map<number, LibroConid>;
}

/**
 * Salida a mercado de una operación entrada, cancelando sus órdenes OCO (SL `STP` y TP `LMT`).
 * `cerrarOperacion` la usa "Salir Fin de Día"; la salida manual (opesalida-dialog) y la automática
 * (broker.component `crearordenbroker`) usan `cancelarOco` / `cerrarPosicionMetaApi`.
 */
@Injectable({
  providedIn: 'root'
})
export class SalidaOperacionBrokerService {

  constructor(
    private sseService: SseService,
    private oauthService: OAuthService,
    private brokerAdapterFactory: BrokerAdapterFactory
  ) { }

  /** id de orden SL/TP realmente asignado por el broker ('0'/vacío = OCO sintético sin id aún). */
  private idOrden(valor: any): string {
    const v = valor == null ? '' : String(valor).trim();
    return v && v !== '0' ? v : '';
  }

  tieneSl(op: any): boolean {
    return !!this.idOrden(op.orderidsalidastp) || Number(op.pricestp) > 0;
  }

  tieneTp(op: any): boolean {
    return !!this.idOrden(op.orderidsalidalmt) || Number(op.pricelmt) > 0;
  }

  /**
   * Cancela en el broker el SL/TP (OCO) de la operación antes de una salida a mercado.
   * IBKR: cancela las STP/LMT vivas (ids de BD + búsqueda en vivo por order_ref); si alguna
   * cancelación falla, el observable falla y el llamador NO debe enviar la salida.
   * MetaAPI: el SL/TP va pegado a la posición y desaparece al cerrarla por positionId; las pendientes
   * ligadas se anulan en `cerrarPosicionMetaApi`, así que aquí no hay nada que cancelar.
   */
  cancelarOco(op: any, wallet: any, licencia: string): Observable<any> {
    if (wallet?.tipoBroker === 'METAAPI') return of(null);
    return this.cancelarOcoIbkr(op, wallet, licencia);
  }

  /**
   * Entradas ejecutadas sin salida enviada, una por isooper y sentido. En bajista el tipo crudo se
   * invierte (misma convención que suscripciones-confirmar.transformarOperaciones), así "ENTRADA" es
   * siempre la apertura. El isooper puede repetirse entre sentidos (contador por cartera/compañía),
   * por eso la clave lleva el sentido. El SQL de operaciones/{idcartera} puede repetir la misma
   * operación (JOINs con varias órdenes): una fila por clave para no mandar dos cierres.
   *
   * Solo cierra la entrada una SALIDA con orden en el broker (`estado` = status de tbl_orden, ejecutada
   * o viva: no se duplica una salida en curso). Una SALIDA sin orden es la señal de salida del modelo
   * aún no enviada (tmp_operacion PENDIENTE-ENVIO...): la posición sigue abierta y se marca en
   * `salidaPendiente` con su estado de envío.
   */
  operacionesAbiertas(ops: any[], idcartera: number): any[] {
    const clave = (o: any) => o.isooper + '|' + (o.bajista ? 'B' : 'A');
    const propias = (ops || [])
      .filter(o => o.idcartera === idcartera)
      .map(o => ({
        ...o,
        estado: o.estado ? String(o.estado).toUpperCase() : 'N/A',
        tipoOperacion: o.bajista
          ? (o.tipoOperacion === 'ENTRADA' ? 'SALIDA' : 'ENTRADA')
          : o.tipoOperacion
      }));
    const salidas = propias.filter(o => o.tipoOperacion === 'SALIDA');
    const conSalida = new Set(salidas.filter(o => o.estado !== 'N/A').map(clave));
    const pendientes = new Map<string, SalidaPendiente>();
    salidas
      .filter(o => o.estado === 'N/A')
      .forEach(o => {
        if (!pendientes.has(clave(o))) {
          pendientes.set(clave(o), { estadoEnvio: o.estadoEnvio || 'PENDIENTE-ENVIO', fechaOperacion: o.fechaOperacion });
        }
      });
    const abiertas = new Map<string, any>();
    propias
      .filter(o => o.tipoOperacion === 'ENTRADA' && ESTADOS_FILLED.includes(o.estado) && !conSalida.has(clave(o)))
      .forEach(o => {
        if (!abiertas.has(clave(o))) abiertas.set(clave(o), { ...o, salidaPendiente: pendientes.get(clave(o)) ?? null });
      });
    return Array.from(abiertas.values());
  }

  /** Suma con signo (+ alcista / − bajista) de las cantidades abiertas del conid. */
  netoEsperado(abiertas: any[], conid: number): number {
    return abiertas
      .filter(o => Number(o.conid) === Number(conid))
      .reduce((acc, o) => acc + this.cantidadConSigno(o), 0);
  }

  /**
   * El broker cubre lo que la BD tiene abierto: mismo signo y al menos esa cantidad (el exceso pueden
   * ser posiciones ajenas a la cartera), o ambos a 0 (alcista y bajista compensadas en IBKR). Si no,
   * algo se cerró sin que la BD lo sepa (p. ej. saltó el SL) y una salida abriría la posición contraria.
   */
  posicionCubre(libro: LibroConid): boolean {
    const EPS = 1e-6;
    if (Math.abs(libro.esperado) < EPS) return Math.abs(libro.neto) < EPS;
    return Math.sign(libro.neto) === Math.sign(libro.esperado) && Math.abs(libro.neto) + EPS >= Math.abs(libro.esperado);
  }

  mensajeNoCuadra(op: any, libro: LibroConid): string {
    return `La posición en IBKR (${libro.neto}) no cuadra con las operaciones abiertas (${libro.esperado}) de ${op.nombreCompania ?? op.ticker ?? op.isooper}`;
  }

  private cantidadConSigno(op: any): number {
    return (op.bajista ? -1 : 1) * Number(op.cantidadacciones ?? 0);
  }

  cerrarOperacion(op: any, wallet: any, licencia: string, ctx?: ContextoSalidaIbkr, origen?: OrigenOrden): Observable<ResultadoSalida> {
    const obs = wallet?.tipoBroker === 'METAAPI'
      ? this.cerrarMetaApi(op, wallet, licencia, origen)
      : this.cerrarIbkr(op, wallet, licencia, ctx, origen);
    return obs.pipe(
      map((): ResultadoSalida => ({ ok: true, msg: 'Cerrada' })),
      catchError((err: any) => of<ResultadoSalida>({ ok: false, msg: this.mensajeError(err) }))
    );
  }

  // ── MetaAPI ────────────────────────────────────────────────────────────────
  // 1) POSITION_CLOSE_ID (el broker elimina SL/TP con la posición)
  // 2) marcar en BD los STP/LMT como cancelados (mejor esfuerzo, no bloquea)
  // 3) registrar la salida en BD
  private cerrarMetaApi(op: any, wallet: any, licencia: string, origen?: OrigenOrden): Observable<any> {
    const symbol = String(op.nombreCompania ?? op.ticker ?? '').split(' CFD')[0];
    return this.cerrarPosicionMetaApi(op, wallet, licencia).pipe(
      switchMap((res: any) => this.sseService.apiPostNew('broker/orderNuevo', [{
        ticker: symbol,
        side: op.bajista ? 'BUY' : 'SELL',
        quantity: op.cantidadacciones,
        acctId: wallet.nombre,
        orderType: 'MKT',
        orderId: res.positionId,
        status: 'Submitted',
        secType: op.conid + ':' + op.secType,
        cOID: this.cOID(op),
        origen,
      }], licencia)),
      // La posición ya está cerrada en el broker: si el backend no registra la salida, avisarlo
      map((r: any) => {
        if (r && r.status === false) {
          throw new Error('Cerrada en MetaTrader pero no registrada: ' + (r.msg || 'sincronice la cartera'));
        }
        return r;
      })
    );
  }

  /**
   * Cierra la posición MetaAPI por positionId (el broker elimina su SL/TP con ella) y marca en BD
   * los STP/LMT sintéticos como cancelados. Emite la respuesta de MetaAPI con `positionId` siempre
   * informado; el registro de la salida (`broker/orderNuevo`) queda a cargo del llamador y debe usar
   * ese `positionId` como orderId: con el `orderId` del ticket de cierre el backend no reconoce la
   * salida al procesar el deal y registra otra SALIDA desde el STP.
   */
  cerrarPosicionMetaApi(op: any, wallet: any, licencia: string): Observable<any> {
    const accountId = this.brokerAdapterFactory.getAccountId(wallet);
    const adapter = this.brokerAdapterFactory.getAdapter(wallet);

    // tbl_orden.acctid guarda la cuenta del broker (login MT5 = wallet.nombre), no el id de MetaAPI.
    // bajista: el isooper puede repetirse entre sentidos.
    return this.sseService.apiPostNew('metaapi/obtenerpositionid', { accountId: wallet.nombre, isooper: op.isooper, bajista: !!op.bajista }, licencia).pipe(
      switchMap((result: any) => {
        if (!result?.positionId) return throwError(() => new Error('No se encontró la posición en MetaAPI'));
        const positionId = String(result.positionId);
        return this.prepararCierreMetaApi(op, positionId, accountId, adapter, licencia).pipe(
          switchMap(() => adapter.placeOrder(accountId, { actionType: 'POSITION_CLOSE_ID', positionId })),
          switchMap((res: any) => {
            if (res?.error) {
              return throwError(() => res.error);
            }
            if (res?.stringCode && res.stringCode !== 'TRADE_RETCODE_DONE') {
              return throwError(() => new Error(res.message ?? res.stringCode));
            }
            return this.cancelarOcoMetaApiEnBd(positionId, licencia).pipe(map(() => ({ ...res, positionId })));
          })
        );
      })
    );
  }

  /**
   * Antes de POSITION_CLOSE_ID: la posición debe seguir abierta en MetaAPI (si el SL/TP ya la cerró,
   * se marcan sus STP/LMT como cancelados en BD y no se registra otra salida) y se anulan en el broker
   * las órdenes pendientes de la operación: las registradas como su STP/LMT o ligadas a su positionId.
   * No se anula nada solo por símbolo: las pendientes no llevan isooper y podrían ser de otro modelo.
   */
  private prepararCierreMetaApi(op: any, positionId: string, accountId: string, adapter: BrokerAdapter, licencia: string): Observable<any> {
    return adapter.getPositions(accountId).pipe(
      switchMap(posiciones => {
        // getPositions devuelve [] también si MetaAPI falla: sin respuesta no se toca la BD
        if (posiciones.length === 0) {
          return throwError(() => new Error('No se encontró la posición en MetaAPI (cerrada o sin respuesta del broker)'));
        }
        const posicion = posiciones.find(p => String(p.raw?.id ?? p.conid ?? '') === positionId);
        if (!posicion) {
          return this.cancelarOcoMetaApiEnBd(positionId, licencia).pipe(
            switchMap(() => throwError(() => new Error('La posición ya no está abierta en MetaAPI')))
          );
        }
        // Con alcista y bajista del mismo valor (cuenta con cobertura) no cerrar la del otro sentido
        const tipo = String(posicion.raw?.type ?? '').toUpperCase();
        const tipoEsperado = op.bajista ? 'POSITION_TYPE_SELL' : 'POSITION_TYPE_BUY';
        if (tipo && tipo !== tipoEsperado) {
          return throwError(() => new Error('La posición ' + positionId + ' de MetaAPI es del sentido contrario a la operación'));
        }
        return adapter.getOrders(accountId);
      }),
      switchMap((ordenes: BrokerOrder[]) => {
        const ids = new Set([this.idOrden(op.orderidsalidastp), this.idOrden(op.orderidsalidalmt)].filter(id => !!id));
        const pendientes = ordenes.filter(o => ids.has(o.orderId) || String(o.raw?.positionId ?? '') === positionId);
        return pendientes.reduce(
          (acc, o) => acc.pipe(switchMap(() => adapter.cancelOrder(accountId, o.orderId).pipe(
            switchMap((res: any) => res?.error || (res?.stringCode && res.stringCode !== 'TRADE_RETCODE_DONE')
              ? throwError(() => new Error('No se pudo anular la orden pendiente ' + o.orderId + ' en MetaAPI'))
              : of(res))
          ))),
          of(null) as Observable<any>
        );
      })
    );
  }

  /**
   * Marca en BD el STP y el LMT sintéticos de la posición como cancelados. Se intentan ambos
   * siempre: la salida manual y la automática no traen pricestp/pricelmt para saber cuál existe,
   * y cancelar uno inexistente no tiene efecto.
   */
  private cancelarOcoMetaApiEnBd(positionId: string, licencia: string): Observable<any> {
    const cancelaciones: Observable<any>[] = ['STP', 'LMT'].map(order_type =>
      this.sseService.apiPostNew('metaapi/cancelarOrden',
        { order_id: Number(positionId), price: 0, pricelmt: 0, order_type }, licencia));
    // Secuencial y sin propagar errores: la posición ya está cerrada en el broker.
    return cancelaciones.reduce(
      (acc, c) => acc.pipe(switchMap(() => c.pipe(catchError(() => of(null))))),
      of(null) as Observable<any>
    );
  }

  // ── IBKR ───────────────────────────────────────────────────────────────────
  // 1) si ya hay una salida viva en el broker para la operación, no se envía otra
  // 2) cancelar STP/LMT vivos y esperar a que IBKR confirme la cancelación; si alguna falla
  //    o la STP/LMT ya se ejecutó, NO se envía la salida
  // 3) comprobar que la posición neta del conid cubre lo abierto en BD (si el SL ya saltó y la BD
  //    no se enteró, un MKT abriría una posición contraria; con alcista y bajista compensadas el
  //    neto es 0 y la salida es válida)
  // 4) registrar en BD + enviar MKT contraria + confirmar avisos (/iserver/reply)
  private cerrarIbkr(op: any, wallet: any, licencia: string, ctx?: ContextoSalidaIbkr, origen?: OrigenOrden): Observable<any> {
    const ladoSalida = op.bajista ? 'BUY' : 'SELL';
    return this.ordenesVivasIbkr(op).pipe(
      switchMap(vivas => {
        if (vivas.some(o => !this.esOco(o) && this.lado(o) === ladoSalida)) {
          return throwError(() => new Error('Ya hay una salida pendiente en el broker'));
        }
        return this.cancelarIdsIbkr(this.idsOcoIbkr(op, vivas), wallet, licencia);
      }),
      switchMap(() => this.libroIbkr(op, wallet, licencia, ctx)),
      switchMap(libro => {
        if (!this.posicionCubre(libro)) return throwError(() => new Error(this.mensajeNoCuadra(op, libro)));
        return this.enviarSalidaIbkr(op, wallet, licencia, origen).pipe(
          // La siguiente salida del mismo conid en la tanda no relee IBKR, que tarda en reflejar el fill
          tap(() => {
            const s = this.cantidadConSigno(op);
            libro.neto -= s;
            libro.esperado -= s;
          })
        );
      })
    );
  }

  /** Libro del conid: el de la tanda si ya se leyó; si no, posición de IBKR + abiertas de la cartera. */
  libroIbkr(op: any, wallet: any, licencia: string, ctx?: ContextoSalidaIbkr): Observable<LibroConid> {
    const conid = Number(op.conid);
    const existente = ctx?.libros.get(conid);
    if (existente) return of(existente);
    const abiertas$: Observable<any[]> = ctx
      ? of(ctx.abiertas)
      : (this.sseService.apiGetWithAuth('operaciones/' + wallet.idcartera, licencia) as Observable<any[]>).pipe(
          map(ops => this.operacionesAbiertas(ops, wallet.idcartera))
        );
    return abiertas$.pipe(
      switchMap(abiertas => this.posicionNetaIbkr(wallet, conid).pipe(
        map(neto => {
          const libro: LibroConid = { neto, esperado: this.netoEsperado(abiertas, conid) };
          ctx?.libros.set(conid, libro);
          return libro;
        })
      ))
    );
  }

  /**
   * Ids de BD (`orderidsalidastp/lmt`, solo los trae `operaciones/{idcartera}`) unidos a las STP/LMT
   * vivas en IBKR de la misma operación: la salida manual y la automática no traen esos ids y los de
   * BD pueden estar a 0 o desfasados.
   */
  private cancelarOcoIbkr(op: any, wallet: any, licencia: string): Observable<any> {
    return this.ordenesVivasIbkr(op).pipe(
      switchMap(vivas => this.cancelarIdsIbkr(this.idsOcoIbkr(op, vivas), wallet, licencia))
    );
  }

  private idsOcoIbkr(op: any, vivas: any[]): string[] {
    const idsBd = [this.idOrden(op.orderidsalidastp), this.idOrden(op.orderidsalidalmt)].filter(id => !!id);
    const idsVivos = vivas.filter(o => this.esOco(o)).map(o => String(o.orderId ?? o.order_id ?? '')).filter(id => !!id);
    return Array.from(new Set([...idsBd, ...idsVivos]));
  }

  private cancelarIdsIbkr(ids: string[], wallet: any, licencia: string): Observable<any> {
    return ids.reduce(
      (acc, id) => acc.pipe(switchMap(() => this.cancelarOrdenIbkr(id, wallet, licencia))),
      of(null) as Observable<any>
    );
  }

  private esOco(o: any): boolean {
    return ['STP', 'STOP', 'LMT', 'LIMIT'].includes(String(o.origOrderType ?? o.orderType ?? '').toUpperCase());
  }

  private lado(o: any): string {
    const s = String(o.side ?? '').toUpperCase();
    return s === 'S' || s === 'SELL' ? 'SELL' : 'BUY';
  }

  /**
   * Órdenes activas en IBKR (de cualquier tipo) cuyo order_ref es de la misma operación (isooper) y
   * sentido. La primera lectura de /iserver/account/orders puede venir incompleta o de caché: se fuerza
   * el refresco con `force=true` y se usa una segunda lectura.
   */
  private ordenesVivasIbkr(op: any): Observable<any[]> {
    const isooper = String(op.isooper ?? '');
    const sentido = op.bajista ? 'B' : 'A';
    const conid = Number(op.conid);
    return this.oauthService.apiGetIbrk('/iserver/account/orders?force=true').pipe(
      catchError(() => of(null)),
      switchMap(() => timer(SalidaOperacionBrokerService.ESPERA_IBKR_MS)),
      switchMap(() => this.oauthService.apiGetIbrk('/iserver/account/orders')),
      map((data: any) => {
        if (data?.error) throw new Error('No se pudieron consultar las órdenes de IBKR: ' + data.error);
        const orders: any[] = data?.orders ?? (Array.isArray(data) ? data : []);
        return orders.filter(o => {
          // order_ref: nctminvst<ts><idNectum>-<isooper>:<A|B>[-SL / -TP]
          return isooperDeOrderRef(o.order_ref) === isooper
            && sentidoDeOrderRef(o.order_ref).toUpperCase() === sentido
            && (!conid || Number(o.conid) === conid)
            && SalidaOperacionBrokerService.ESTADOS_ACTIVOS.includes(String(o.status ?? ''));
        });
      })
    );
  }

  /**
   * Posición neta del conid en IBKR (0 si no hay fila: alcista y bajista compensadas o cerrada).
   * Se invalida antes la caché de posiciones de IBKR, que tarda en reflejar un fill.
   */
  posicionNetaIbkr(wallet: any, conid: number): Observable<number> {
    return this.oauthService.apiPostIbrk('/portfolio/' + wallet.nombre + '/positions/invalidate', {}).pipe(
      catchError(() => of(null)),
      switchMap(() => this.oauthService.apiGetIbrk('/portfolio/' + wallet.nombre + '/positions/0')),
      map((data: any) => {
        if (!Array.isArray(data)) {
          throw new Error('No se pudo comprobar la posición en IBKR' + (data?.error ? ': ' + data.error : ''));
        }
        return data
          .filter(p => Number(p.conid) === Number(conid))
          .reduce((acc, p) => acc + Number(p.position ?? 0), 0);
      })
    );
  }

  private static readonly ESTADOS_ACTIVOS = ['PendingSubmit', 'PreSubmitted', 'Submitted'];
  private static readonly ESPERA_IBKR_MS = 500;
  private static readonly INTENTOS_CANCELACION = 5;

  /**
   * DELETE de la orden y espera a que IBKR la dé por cancelada: el DELETE solo pide la cancelación,
   * y si la STP/LMT se ejecuta en ese hueco habría dos salidas. Si acaba Filled, la posición ya se
   * cerró y el llamador no debe enviar otra salida.
   * "Order is filled or canceled": el id de BD está desfasado (p. ej. cancelada fuera de la app); su
   * estado real decide (cancelada = seguir y actualizar BD, ejecutada = posición cerrada).
   */
  private cancelarOrdenIbkr(orderId: string, wallet: any, licencia: string): Observable<any> {
    return this.oauthService.apiDeleteIbrk('/iserver/account/' + wallet.nombre + '/order/' + orderId).pipe(
      switchMap((data: any) => {
        if (data?.error && !/filled or cancel/i.test(String(data.error))) {
          return throwError(() => new Error('No se pudo cancelar la orden OCO ' + orderId + ': ' + data.error));
        }
        return this.esperarCancelacionIbkr(orderId, licencia, data?.error ? SalidaOperacionBrokerService.INTENTOS_CANCELACION : 1);
      })
    );
  }

  private esperarCancelacionIbkr(orderId: string, licencia: string, intento: number): Observable<any> {
    return timer(SalidaOperacionBrokerService.ESPERA_IBKR_MS).pipe(
      switchMap(() => this.oauthService.apiGetIbrk('/iserver/account/order/status/' + orderId)),
      catchError(() => of(null)),
      switchMap((st: any) => {
        const estado = String(st?.order_status ?? st?.status ?? '');
        if (['Cancelled', 'Inactive'].includes(estado)) {
          // Actualizar BD con el estado real de la orden (mejor esfuerzo)
          return this.sseService.apiPostNew('broker/actualizarOrdenPrecio', st, licencia).pipe(catchError(() => of(null)));
        }
        if (estado === 'Filled') {
          // Id de BD que no es una STP/LMT (p. ej. la propia entrada): no es un SL/TP que cancelar
          if (st?.order_type && !this.esOco({ orderType: st.order_type })) return of(null);
          return this.sseService.apiPostNew('broker/actualizarOrdenPrecio', st, licencia).pipe(
            catchError(() => of(null)),
            switchMap(() => throwError(() => new Error('La orden OCO ' + orderId + ' ya se ejecutó: la posición está cerrada')))
          );
        }
        if (intento >= SalidaOperacionBrokerService.INTENTOS_CANCELACION) {
          return throwError(() => new Error('IBKR no confirmó la cancelación de la orden OCO ' + orderId + (estado ? ' (' + estado + ')' : '')));
        }
        return this.esperarCancelacionIbkr(orderId, licencia, intento + 1);
      })
    );
  }

  private enviarSalidaIbkr(op: any, wallet: any, licencia: string, origen?: OrigenOrden): Observable<any> {
    const cOID = this.cOID(op);
    const ordersData = {
      orders: [{
        orderType: 'MKT',
        ticker: String(op.nombreCompania ?? op.ticker ?? '').split(' CFD')[0],
        side: op.bajista ? 'BUY' : 'SELL',
        quantity: op.cantidadacciones,
        remainingquantity: 0,
        acctId: wallet.nombre,
        cOID,
        secType: op.conid + ':' + op.secType,
        filledquantity: 0,
        parentId: cOID,
        tif: 'GTC',
        outsideRTH: false,
        referrer: 'modelosdeinversion',
        useAdaptive: false,
        conid: Number(op.conid),
        price: 0
      }]
    };
    // El origen solo se registra en BD: ordersData va tal cual a IBKR
    return this.sseService.apiPostNew('broker/orderNuevo', ordersData.orders.map(o => ({ ...o, origen })), licencia).pipe(
      switchMap((r: any) => {
        if (!r?.status) return throwError(() => new Error(r?.msg ?? 'No se pudo registrar la salida'));
        return this.oauthService.apiPostIbrk('/iserver/account/' + wallet.nombre + '/orders', ordersData);
      }),
      switchMap((resp: any) => this.resolverRespuestaIbkr(resp, licencia, 0))
    );
  }

  /** Confirma avisos de IBKR en cadena y, al obtener estado de orden, lo persiste en BD. */
  private resolverRespuestaIbkr(resp: any, licencia: string, profundidad: number): Observable<any> {
    const r = Array.isArray(resp) ? resp[0] : null;
    if (!r) {
      return throwError(() => new Error(resp?.error ?? 'Respuesta inesperada de IBKR'));
    }
    if (r.message && r.id) {
      if (profundidad >= 5) return throwError(() => new Error('Demasiadas confirmaciones de IBKR: ' + r.message));
      return this.oauthService.apiPostIbrk('/iserver/reply/' + r.id, { confirmed: true }).pipe(
        switchMap((next: any) => this.resolverRespuestaIbkr(next, licencia, profundidad + 1))
      );
    }
    if (r.order_status) {
      return this.sseService.apiPostNew('broker/actualizarOrden', resp, licencia).pipe(map(() => r));
    }
    return throwError(() => new Error(r.message ?? 'IBKR no devolvió estado de la orden'));
  }

  private cOID(op: any): string {
    return 'nctminvst' + new Date().getTime() + op.idNectum + '-' + op.isooper + ':' + (op.bajista ? 'B' : 'A');
  }

  private mensajeError(err: any): string {
    return err?.error?.message ?? err?.message ?? (typeof err === 'string' ? err : 'Error desconocido');
  }
}
